import React from "react";
import Chart from "./chart";
import {isNumber, handleDate} from '../../../../utils/utils'
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function getNumberOfAssets(compositions) {
  if (!compositions) return "-";

  const key = Object.keys(compositions)[0];
  const assets = compositions[key];

  if (!assets || assets.length === 0) return "-";
  if (assets[0]?.asset_name === "Available for subscribers only") return "-";

  return assets.length;
}

export default function AboutTable({ index }) {

  const { t, i18n } = useTranslation();
  const isLtr = i18n.language === 'en';
  const getExtraDataField = (subSection, field) => {
    const extraData = index.extra_data_ || {};
    const mainKey = Object.keys(extraData)[0];
    const subSectionData = extraData[mainKey]?.[subSection] || [];
  
    if (!field) {
      return subSectionData;
    }
  
    if (subSectionData.length === 0) {
      return "-";
    }
  
    const matchedItem = subSectionData.find((item) => item["metric"] === field);
    return matchedItem ? matchedItem["value"] : "-";
  };
  
  const formatValue = (value, suffix = '', decimalPlaces = 2) => {
    if (isNumber(value)) {
      return `${value.toFixed(decimalPlaces)}${suffix}`;
    }
    return "-";
  };
  

  const renderRow = (label, value, tdClassName="index-table-about-td", suffix='', decimalPlaces=2) => {
    let displayValue = "-";
    if (value !== undefined && value !== null) {
      if (typeof value === "number") {
        displayValue = value.toFixed(decimalPlaces);
      } else {
        displayValue = value;
      }
    }
    if (displayValue === "NaN %") {
      displayValue = "-";
    }
  
    return (
      <tr className="index-table-about-row">
        <th className="index-table-about-th"
        >{t(label)}</th>
        <td className={isLtr ? tdClassName : `index-table-about-td_he`}
        >{displayValue}{suffix}</td>
      </tr>
    );
  };
  

  const downloadPDF = (binaryString, fileName) => {
    if (!binaryString || !fileName) return;
  
    const decodedString = window.atob(binaryString);
    const len = decodedString.length;
    const bytes = new Uint8Array(len);
  
    for (let i = 0; i < len; i++) {
      bytes[i] = decodedString.charCodeAt(i);
    }
  
    const blob = new Blob([bytes], { type: "application/pdf" });
  
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };
  
  const renderAdditionalInfo = () => (
    <tr className="index-table-about-row">
      <th className="index-table-about-th">{t("AboutTable.additional_info_pdf")}</th>
      <td className="index-table-about-td">
        <button
          className="index-table-about-link"
          onClick={() =>
            downloadPDF(index?.pdf_file, index?.pdf_file_name)
          }
        >
          {t("AboutTable.methodology")}
        </button>
        {index?.presentation && (
        <button
          className="index-table-about-link"
          onClick={() =>
            downloadPDF(index?.presentation, index?.presentation_name)
          }
        >
          {t("AboutTable.presentation")}
        </button>)}
        {index?.committee_decision && (
        <button
          className="index-table-about-link"
          onClick={() =>
            downloadPDF(index?.committee_decision, index?.committee_decision_name)
          }
        >
          {t("AboutTable.committee_decision")}
        </button>)}
      </td>
    </tr>
  );

  return (
    <table className="index-table-about mb-30" cellSpacing="0" rules="all" border="1" >
      <tbody>
        {renderRow(t('AboutTable.base_date'), index.base_date ? handleDate(index.base_date) : "")}
        {renderRow("AboutTable.base_value", index.first_price)}
        {renderRow("AboutTable.latest_date", index.latest_date ? handleDate(index.latest_date) : "")}
        {renderRow("AboutTable.latest_value", index.latest_price)}
        {/* number_of_assets needs to be int */}
        {renderRow("AboutTable.number_of_assets",
           index.compositions ? getNumberOfAssets(index.compositions) : "",
           "index-table-about-td",
           "",
           0)}
        {renderRow("AboutTable.weighting_scheme", isLtr ? index.weighting_scheme : index.weighting_scheme_hebrew)}
        {index.gto_ticker && renderRow("AboutTable.ticker", index.gto_ticker)}
        {index?.extra_data_ && Object.keys(index?.extra_data_).length > 0 && (
          <>
            {renderRow("AboutTable.credit_rating_average", getExtraDataField("index_summary", "Average Rating"))}
            {renderRow("AboutTable.min_market_value", index?.min_market_value)}
            {renderRow("AboutTable.min_turnover", index?.min_turnover)}
            {renderRow("AboutTable.index_duration", formatValue(getExtraDataField("index_summary", "Index Duration")))}
            {renderRow("AboutTable.ytm", formatValue(getExtraDataField("index_summary", "YTM"), " %"))}
            {renderRow("AboutTable.ytm_cpi", formatValue(getExtraDataField("index_summary", "YTM CPI"), " %"))}
            {renderRow("AboutTable.ytm_ils", formatValue(getExtraDataField("index_summary", "YTM ILS"), " %"))}
            {renderRow("AboutTable.duration_cpi", formatValue(getExtraDataField("index_summary", "Duration CPI"), " %"))}
            {renderRow("AboutTable.duration_ils", formatValue(getExtraDataField("index_summary", "Duration ILS"), " %"))}
            {renderRow("AboutTable.ils_weight", formatValue(Number(getExtraDataField("index_summary", "ILS weight")) * 100, " %"))}
            {renderRow("AboutTable.cpi_weight", formatValue(Number(getExtraDataField("index_summary", "CPI linked weight")) * 100, " %"))}
            {renderRow("AboutTable.max_weight_for_issuer", formatValue(getExtraDataField("index_summary", "Max Weight for Issuer"), " %"))}
            {renderRow("AboutTable.max_weight_for_bond", formatValue(getExtraDataField("index_summary", "Max Weight for Bond"), " %"))}
            {renderRow("AboutTable.spread_from_gov_bond", formatValue(getExtraDataField("index_summary", "Spread From Gov Bond"), " %"))}
            {renderRow("AboutTable.sectorial_distribution", <Chart data={getExtraDataField("sectorial_distribution", null)} />, "index-table-about-td_chart")}
          </>
        )}
        {renderAdditionalInfo()}
        {index?.research_notes?.length > 0 && (
          <tr className="index-table-about-row">
            <th className="index-table-about-th">{t("AboutTable.research_notes")}</th>
            <td className="index-table-about-td">
              <ul style={{ paddingLeft: "0", paddingRight: "0",listStyle: "none" }}>
                {index.research_notes.map((url) => {
                  const fileName = decodeURIComponent(url.split("/").pop()).replace(/\+/g, " ");
                  return (
                    <li key={url} style={{ marginBottom: "5px" }}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="index-table-about-link"
                        style={{
                          display: "inline-block",
                          wordBreak: "break-word",
                          maxWidth: "100%",
                        }}
                      >
                        {fileName}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </td>
          </tr>
        )}

      </tbody>
    </table>
  );
  
}
AboutTable.propTypes = {
  index: PropTypes.shape({
    base_date: PropTypes.string,
    first_price: PropTypes.number,
    latest_date: PropTypes.string,
    latest_price: PropTypes.number,
    compositions: PropTypes.object,
    weighting_scheme_hebrew: PropTypes.string,
    weighting_scheme: PropTypes.string,
    gto_ticker: PropTypes.string,
    description: PropTypes.string,
    extra_data_: PropTypes.object,
    pdf_file: PropTypes.string,
    pdf_file_name: PropTypes.string,
    min_market_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    min_turnover: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};